import { z } from 'zod';
import { ControlVariableDataType } from 'libs/end-to-end/src/types/databases/controls/control-variable-data-type';
import { ControlVariableTransformationType } from 'libs/end-to-end/src/types/databases/controls/control-variable-transformation-type';
import { ControlVariablePlaceToHideValue } from 'libs/end-to-end/src/types/databases/controls/control-variable-places-to-hide';

export const NumericalTraitDefinitionUnitScoring = z
  .object({
    actualMaximumValue: z.number(),
    displayMaximumValue: z.number(),
  })
  .describe('NumericalTraitDefinitionUnitScoring');

export const NumericalTraitDefinition = z
  .object({
    /**
     * @example 'year' or 'month'
     */
    unitSingular: z.string().min(1),
    /**
     * @example 'years' or 'months'
     */
    unitPlural: z.string().min(1),
    /**
     * The lowest value the trait can have
     * @example 1 // Will only show whole numbers
     * @example .5 // Will only show numbers divisible by .5
     */
    denominator: z.number().positive(),

    /**
     * Allows turning a value like 0.42 (actual maximum 1) into a score like (42/100) (display maximum 100).
     * Useful for displaying scores instead of raw values.
     */
    unitScoring: NumericalTraitDefinitionUnitScoring.optional(),
  })
  .describe('NumericalTraitDefinition');

export type NumericalTraitDefinition = z.infer<typeof NumericalTraitDefinition>;

export const CategoricalTraitDefinition = z
  .object({
    sortOrder: z.array(z.string()).optional(),
  })
  .describe('CategoricalTraitDefinition');

export type CategoricalTraitDefinition = z.infer<
  typeof CategoricalTraitDefinition
>;

export const ControlVariableDefinition = z
  .object({
    columnHeaderValue: z.string(),
    displayName: z.string(),
    dataType: ControlVariableDataType,
    transformationType: ControlVariableTransformationType,
    numericalTraitDefinition: NumericalTraitDefinition.optional(),
    categoricalTraitDefinition: CategoricalTraitDefinition.optional(),
    description: z.string().optional(),
    placesToHide: z.array(ControlVariablePlaceToHideValue).optional(),
  })
  .describe('ControlVariableDefinition');

export type ControlVariableDefinition = z.infer<
  typeof ControlVariableDefinition
>;
